// Display formatting. Money helpers come from @convenor/shared (formatEur etc.).
import { dayMonthYearShort, formatFixed, formatNumber, getNumberFormat, numberInputError, readNumber } from "@convenor/shared";
export { formatEur, formatHalves, formatNumber } from "@convenor/shared";

const longDate = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" });
const dateTime = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

const toDate = (v: string) => (/^\d{4}-\d{2}-\d{2}$/.test(v) ? new Date(`${v}T00:00:00Z`) : new Date(v));

/** "12 Mar 2026" (tables). Accepts "YYYY-MM-DD" or an ISO timestamp. X-18: "Sep", never "Sept" (the shared month list). */
export const formatDate = (v: string | null | undefined): string => (v ? dayMonthYearShort(toDate(v).toISOString()) : "");

/** "12 March 2026" (documents). */
export const formatDateLong = (v: string | null | undefined): string => (v ? longDate.format(toDate(v)) : "");

/** "12 Mar 2026, 14:05" in the viewer's time zone. */
export const formatDateTime = (v: string | null | undefined): string => (v ? dateTime.format(new Date(v)).replace(/\bSept\b/, "Sep") : "");

/** "12 Mar 2026 to 30 Sep 2029", "12 Mar 2026", or "" */
export function formatPeriod(start: string | null, end: string | null): string {
  if (start && end && start !== end) return `${formatDate(start)} to ${formatDate(end)}`;
  return formatDate(start ?? end);
}

/** 1 -> "1 day", 9 -> "9 days" */
export const formatDays = (n: number): string => `${n} ${n === 1 ? "day" : "days"}`;

/** 2.5 -> "2,5", 3 -> "3" (half-unit quantities, for display; inputs keep `String(n)`). */
export const formatQuantity = (n: number): string => (Number.isInteger(n) ? String(n) : formatFixed(n));

/**
 * "2,5" or "2.5" -> 2.5; "" -> null; anything else -> NaN (also below zero).
 * The shared `readNumber`, so "1,000" or "1.000" (thousands or decimals?) is
 * refused, never read as 1 (R2-008: people may write either style).
 */
export function parseQuantity(input: string): number | null {
  const n = readNumber(input);
  return n !== null && n < 0 ? Number.NaN : n;
}

/**
 * A number typed in a form (days, units, a percentage), read with the shared
 * `readNumber`: "2,5" and "2.5" are 2.5, "1 000" is 1000, and "1,000" or
 * "1.000" is refused (NaN) rather than guessed. `empty` is what an empty box
 * gives (NaN by default, so a required field fails its check).
 */
export function readTyped(input: string, empty = Number.NaN): number {
  const n = readNumber(input);
  return n === null ? empty : n;
}

/** The messages for typed numbers `readTyped` refuses, by field: `{ days: "\"1,000\" could mean 1000 or 1. …" }`. */
export function typedNumberErrors(values: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [field, input] of Object.entries(values)) {
    const message = numberInputError(input);
    if (message) out[field] = message;
  }
  return out;
}

/** A figure the API wrote, always European ("1.152", "489,5", "2.400.000"); integers without a separator need no change. */
const EUROPEAN_FIGURE = /\b\d{1,3}(?:\.\d{3})+(?:,\d+)?\b|\b\d+,\d+\b/g;

/**
 * R2-008: figures in a text the API wrote (always European, F1) rewritten in the person's number
 * format: "489,5 of 1.152" -> "489.5 of 1,152" for North American, unchanged for European. For
 * server texts that only carry counts and days (Home's "Project at a glance").
 */
export function fromEuropean(text: string): string {
  if (getNumberFormat() === "eu") return text;
  return text.replace(EUROPEAN_FIGURE, (token) => {
    const [whole, fraction = ""] = token.split(",");
    return formatNumber(Number(`${whole.replace(/\./g, "")}.${fraction || "0"}`), fraction.length, fraction.length);
  });
}

/** "Ana Silva" -> "Ana" */
export const firstName = (name: string): string => name.trim().split(/\s+/)[0] ?? name;
